import { Box, Typography, Stack } from '@mui/material';
import {
  ModeEditOutlineOutlined as EditIcon,
  RestartAltOutlined as ResetPasswordIcon,
  ChangeCircleOutlined,
  SendOutlined as InviteIcon,
} from '@mui/icons-material';

import {
  FHButton,
  FHChip,
  FHConfirmDialog,
  FHSkeletonLoader,
} from '../../../../components/base';
import { FORTA_SUCCESS, FORTA_WHITE, FORTA_ORANGE } from '../../../../utils/colors';
import { areUserRolesIn, history } from '../../../../helpers';
import { Roles, Themes } from '../../../../constants';
import {
  ProviderClinicalStatus,
  QaBillingStatus,
} from '../../../constants/general';
import {
  useProviderDetailsState,
  useResetProviderPassword,
  useProviderInviteToPlatform,
} from '../hooks';
import { PROVIDER_STATUS_CHIP_SX } from '../styles';

export function ProviderDetailsHeader() {
  const { provider, loading, user } = useProviderDetailsState();
  const {
    cannotSendInvitePopup,
    resetProviderPassword,
    closeCannotSendInvitePopup,
  } = useResetProviderPassword();
  const { showInviteToPlatform, isInviting, handleInviteToPlatform } =
    useProviderInviteToPlatform();

  const clinicalProvider = provider?.clinical_status === ProviderClinicalStatus.CLINICAL;
  const billableProvider = provider?.qa_billing_status === QaBillingStatus.BILLABLE;

  const handleEdit = () => {
    history.navigate(`/admin/provider/${provider?.id}/edit`);
  };

  const handleChangeBillingStatus = () => {
    history.navigate(`/admin/change-billing-status/${provider?.id}`);
  };

  return (
    <>
      <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
        {loading ? (
          <FHSkeletonLoader width={200} height={24} />
        ) : (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <Typography variant="h3">
              {provider?.first_name} {provider?.last_name}
            </Typography>
            <FHChip
              sx={PROVIDER_STATUS_CHIP_SX}
              content={provider?.clinical_status}
              bgColor={clinicalProvider ? FORTA_SUCCESS : FORTA_ORANGE}
              color={FORTA_WHITE}
            />
            {(provider?.clinical_status === ProviderClinicalStatus.OFFBOARDED ||
              provider?.clinical_status === ProviderClinicalStatus.LIMITED_ACCESS_GUARDIAN) &&
              provider?.clinical_status_reason && (
                <FHChip
                  sx={PROVIDER_STATUS_CHIP_SX}
                  content={provider?.clinical_status_reason}
                  bgColor={FORTA_ORANGE}
                  color={FORTA_WHITE}
                />
              )}
            <FHChip
              sx={PROVIDER_STATUS_CHIP_SX}
              content={provider?.qa_billing_status?.toUpperCase()}
              bgColor={billableProvider ? FORTA_SUCCESS : FORTA_ORANGE}
              color={FORTA_WHITE}
            />
          </Box>
        )}
        {areUserRolesIn(user?.roles ?? [], [Roles.Admin, Roles.OnboardingCoordinator]) && (
          <Stack direction="row" spacing={2}>
            <FHButton
              startIcon={<EditIcon />}
              disabled={loading}
              onClick={handleEdit}
              data-testid="editProvider"
            >
              Edit
            </FHButton>
            {clinicalProvider && (
              <FHButton
                startIcon={<ChangeCircleOutlined />}
                disabled={loading}
                onClick={handleChangeBillingStatus}
                data-testid="changeBillingStatus"
              >
                Change billing status
              </FHButton>
            )}
            {clinicalProvider && (
              <FHButton
                startIcon={<ResetPasswordIcon />}
                disabled={loading}
                onClick={resetProviderPassword}
                data-testid="resetProviderPassword"
              >
                Re-invite user
              </FHButton>
            )}
            {showInviteToPlatform && (
              <FHButton
                startIcon={<InviteIcon />}
                disabled={loading || isInviting}
                onClick={handleInviteToPlatform}
                data-testid="inviteProviderToPlatform"
              >
                Invite to platform
              </FHButton>
            )}
          </Stack>
        )}
      </Box>
      <FHConfirmDialog
        open={cannotSendInvitePopup}
        colorTheme={Themes.Primary}
        content="An invite cannot be sent since the BT doesn't have an email associated with them."
        submittext="OKAY"
        hideCancelButton={true}
        onClose={closeCannotSendInvitePopup}
        onSubmit={closeCannotSendInvitePopup}
      />
    </>
  );
}
